import {useMutation, useSuspenseQuery} from "@apollo/client";
import classnames from "classnames";
import {sum} from "lodash";
import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {redirect, useLocation, useNavigate} from "react-router";

import {Alert} from "@/components/alert/Alert.tsx";
import {useCheckoutContext, useCurrentUserContext} from "@/components/context/hooks.ts";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {trackPurchase} from "@/components/trackers/GoogleTagEvents.ts";
import {GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";
import {
  CheckoutContextResult,
  GQL_ACCOUNT_STATS,
  GQL_CHECKOUT_CONTEXT,
  GQL_SUBSCRIPTION_PACKS,
  GQL_UPDATE_SUBSCRIPTION_V2,
  UpdateSubscriptionV2Response,
} from "@/lib/queries/billing.ts";
import {BillingPeriod, PaymentMethod, PaymentMethodType} from "@/lib/types/billing.ts";
import {PaymentMethodEdit} from "@/pages/billing/payment-method/PaymentMethodEdit.tsx";
import {PaymentMethodSelect} from "@/pages/billing/v2/checkout/PaymentMethodSelect.tsx";
import {SubscriptionPackTierWrapped} from "@/pages/billing/v2/subscriptions/Subscriptions.tsx";
import {CostBreakdownWithPayment} from "@/pages/billing/v2/summary/CostBreadownWithPayment.tsx";

export const CheckoutPageContent = () => {
  const location = useLocation();
  const navigateFn = useNavigate();
  const {user} = useCurrentUserContext();
  if (!location.state) {
    redirect("/billing/?action=subscribe");
  }
  const billingPeriod = location.state.billingPeriod || BillingPeriod.MONTH;
  const items: SubscriptionPackTierWrapped[] = location.state.bundle || [];
  const newSubscriptionPrice = sum(
    items.map((tier) => (billingPeriod === BillingPeriod.MONTH ? tier.price_monthly : tier.price_annual * 12))
  );
  // get billing-related data
  const {data: checkoutContextData} = useSuspenseQuery<CheckoutContextResult>(GQL_CHECKOUT_CONTEXT);
  const {
    checkoutContext: {paymentMethod, billingData, braintreeToken, hasActiveSubscription},
  } = checkoutContextData;

  const [updateSubscription] = useMutation<UpdateSubscriptionV2Response>(GQL_UPDATE_SUBSCRIPTION_V2, {
    refetchQueries: [GQL_USER_CONTEXT, GQL_CHECKOUT_CONTEXT, GQL_SUBSCRIPTION_PACKS, GQL_ACCOUNT_STATS],
  });
  const [updateErrors, setUpdateErrors] = useState<string[]>([]);

  const onPaymentMethodSuccess = async () => {
    // By this time, the payment method is already set, so we only
    // need to submit the checkout request. There still can be a problem
    // with payment provider, or with validation of the checkout order.
    try {
      const result = await updateSubscription({
        variables: {
          billingPeriod,
          selectedTierIds: items.map((tier) => tier.tier_id),
        },
      });
      if (result.data?.updateSubscriptionV2?.success === true) {
        trackPurchase(newSubscriptionPrice, billingPeriod, "", user);
        navigateFn(`/billing/subscription/done?action=subscription&grmkplan=${newSubscriptionPrice}`);
      } else {
        setUpdateErrors(result.data?.updateSubscriptionV2?.errors?.__all__ || []);
      }
    } catch {
      setUpdateErrors(["An error occurred while updating the subscription."]);
    } finally {
      setRequestInProgress(false);
    }
  };
  const formId = "paymentMethodForm";
  const methods = useForm();
  const {setRequestInProgress} = useCheckoutContext();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(paymentMethod);
  const [newPaymentMethodType, setNewPaymentMethodType] = useState<PaymentMethodType>(PaymentMethodType.CREDIT_CARD);
  const initialSubscription = !hasActiveSubscription && !paymentMethod?.type;
  return (
    <FormProvider {...methods}>
      <div className="container">
        <FormErrorAlerts className="mb-3" errors={updateErrors} />
        {!user?.is_primary && user?.has_admin_access && initialSubscription && (
          <div className="row my-3">
            <div className="col-12">
              <Alert size="small" show>
                <p className="text-center m-0">You will take ownership of the account after subscribing.</p>
              </Alert>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-12">
            <h5>Select Payment Method</h5>
            <PaymentMethodSelect
              value={selectedPaymentMethod}
              setValue={setSelectedPaymentMethod}
              existingPaymentMethod={paymentMethod}
            />
          </div>
        </div>
        <div className="row mt-4">
          {!selectedPaymentMethod?.pm_type && (
            <div className="col-7">
              <h5>Add New Payment Method</h5>
              <PaymentMethodEdit
                billingData={billingData || {}}
                braintreeToken={braintreeToken || ""}
                onCancel={() => {}}
                onSuccess={async () => {
                  setSelectedPaymentMethod(paymentMethod);
                  setNewPaymentMethodType(PaymentMethodType.CREDIT_CARD);
                  await onPaymentMethodSuccess();
                }}
                withButtons={false}
                withCancelButton={true}
                formId={formId}
                onTabChange={setNewPaymentMethodType}
                subscription={true}
              />
            </div>
          )}
          {selectedPaymentMethod?.pm_type && (
            <form
              id={formId}
              onSubmit={async (e) => {
                e.preventDefault();
                setRequestInProgress(true);
                await onPaymentMethodSuccess();
              }}
            ></form>
          )}
          <div
            className={classnames({
              "col-5 pe-0": !selectedPaymentMethod?.pm_type,
              "col-6 pe-0": selectedPaymentMethod?.pm_type,
            })}
          >
            <CostBreakdownWithPayment
              title="Order Summary"
              paymentType={selectedPaymentMethod?.pm_type || newPaymentMethodType}
              billingPeriod={billingPeriod}
              items={items}
              formId={formId}
            />
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
